import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { HttpHeaderConstants } from '../../services/http-header.constant';
import { AppLicense } from '../../models/appcloud/app-license.model';
import { AppLicenseSearch } from '../../models/appcloud/app-license-search.model';
import { LicenseConstants } from '../../constants/license.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
    GenericSnackbarComponent,
    GenericSnackbarData,
} from '../../components/snack-bars/generic-snackbar/generic-snackbar.component';

// (TODO before AC3) - two `of<AppLicense>(null);` converted to `of<AppLicense>()` like normal, but does that act the same?
@Injectable({
    providedIn: 'root',
})
export class AppLicenseService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);
    readonly #snackBarService = inject(MatSnackBar);

    private APP_LICENSE_URL: string = environment.publicApiHost + '/appcloud/applicenses';

    getLatest(globalAppId: string, additionalHeaders: any = {}): Observable<AppLicense> {
        const process = 'AppLicenseService.getLatest';
        this.#spinnerService.addProcess(process, 'Retrieving license data...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams(),
        };
        httpOptions.params = httpOptions.params.set('globalAppId', globalAppId);

        return this.#http.get<AppLicense>(this.APP_LICENSE_URL, httpOptions).pipe(
            catchError(error => {
                if (error.status === 404) {
                    this.showNoLicense();
                    return of<AppLicense>();
                } else {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while retrieving data from Relationship One. Please try again later.'
                    );
                    return of<AppLicense>();
                }
            }),
            finalize(() => {
                this.#spinnerService.completeProcess(process);
            })
        );
    }

    checkLicenseStatus(globalAppId: string, additionalHeaders: any = {}) {
        this.#spinnerService.addProcess(
            'AppLicenseService.getByGlobalAppId.' + globalAppId,
            'Retrieving your license information...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('globalAppId', globalAppId),
        };
        return this.#http.get<AppLicense>(this.APP_LICENSE_URL, httpOptions).pipe(
            tap((applicense: AppLicense) => {
                if (applicense.status === LicenseConstants.expired) {
                    this.showExpired(applicense);
                } else if (applicense.status === LicenseConstants.inactive) {
                    this.showInactive(applicense);
                }
            }),
            catchError(error => {
                if (error.status === 404) {
                    this.showNoLicense();
                    return of<AppLicense>();
                } else {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while retrieving configuration data from Relationship One. Please try again later.'
                    );
                    return of<AppLicense>();
                }
            }),
            finalize(() => {
                this.#spinnerService.completeProcess('AppLicenseService.getByGlobalAppId.' + globalAppId);
            })
        );
    }

    findAll(applicenseSearch: AppLicenseSearch, additionalHeaders: any = {}): Observable<AppLicense[]> {
        this.#spinnerService.addProcess('AppLicenseService.findAll', 'Loading your app licenses...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams({ fromObject: <any>applicenseSearch }),
        };

        return this.#http.get<AppLicense[]>(this.APP_LICENSE_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving app license data from Relationship One. Please try again later.'
                );
                return of<AppLicense[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess('AppLicenseService.findAll'))
        );
    }

    private showExpired(applicense: AppLicense): void {
        this.#snackBarService.openFromComponent<GenericSnackbarComponent, GenericSnackbarData>(
            GenericSnackbarComponent,
            {
                data: {
                    messages: [
                        `Warning: Your app license expired on ${this.getLocaleExpiryDateString(
                            applicense.usageEndDate!
                        )} and will become inactive soon.`,
                    ],
                    showDismissButton: true,
                },
                panelClass: ['generic-snack-bar-warning'],
                verticalPosition: 'top',
            }
        );
    }

    private showInactive(applicense: AppLicense): void {
        this.#snackBarService.openFromComponent<GenericSnackbarComponent, GenericSnackbarData>(
            GenericSnackbarComponent,
            {
                data: {
                    messages: [
                        `Your app license expired on ${this.getLocaleExpiryDateString(
                            applicense.usageEndDate!
                        )} and is now inactive. Your configurations for this app will no longer run.`,
                    ],
                    showDismissButton: true,
                },
                panelClass: ['generic-snack-bar-warning'],
                verticalPosition: 'top',
            }
        );
    }

    private showNoLicense() {
        this.#snackBarService.openFromComponent<GenericSnackbarComponent, GenericSnackbarData>(
            GenericSnackbarComponent,
            {
                data: {
                    messages: [`You do not have a license to use this app.`],
                    showDismissButton: true,
                },
                panelClass: ['generic-snack-bar-warning'],
                verticalPosition: 'top',
            }
        );
    }

    private getLocaleExpiryDateString(usageEndDate: string): string {
        return new Date(usageEndDate).toLocaleDateString();
    }
}
